import React, { useEffect, useRef, useState } from "react";
import RefreshIcon from "@material-ui/icons/Refresh";
import { apiFetch } from "../../utils/apiFetch";

import "./global/global.css";

const PAGE_TITLE = "Emails awaiting approval";
const PAGE_SUBTITLE =
  "Our agents draft these emails; none go out until someone here sends them. " +
  "Read each one, then send it or decline it.";

const AGENT_LABELS = {
  deal_coordinator: "Deal Coordinator",
  rental_manager: "Rental Manager",
  concierge: "Concierge",
  marketing_manager: "Marketing Manager",
};

const agentLabel = (email) => email.agent_label || AGENT_LABELS[email.agent] || "Agent";

const AgentTag = ({ email }) => (
  <span
    data-testid="agent-tag"
    style={{
      padding: "1px 6px",
      backgroundColor: "#eceff1",
      color: "#37474f",
      borderRadius: 4,
      fontSize: 11,
      fontWeight: 600,
    }}
  >
    {agentLabel(email)}
  </span>
);

const EMAIL_TYPE_LABELS = {
  reply: "Reply",
  deal_intro: "Introduction",
  milestone_ping: "Task request",
  milestone_question: "Question relay",
  milestone_blocked: "Blocked step",
  completion: "Deal complete",
  print_order_update: "Print order update",
};

const EMAIL_TYPE_COLORS = {
  reply: { bg: "#e3f2fd", color: "#1565c0" },
  deal_intro: { bg: "#e0f7fa", color: "#00695c" },
  milestone_ping: { bg: "#e8f5e9", color: "#2e7d32" },
  milestone_question: { bg: "#ede7f6", color: "#4527a0" },
  milestone_blocked: { bg: "#fff3e0", color: "#e65100" },
  completion: { bg: "#f3e5f5", color: "#6a1b9a" },
  print_order_update: { bg: "#fce4ec", color: "#ad1457" },
};

const EMAIL_TYPE_PURPOSE = {
  reply: "The agent's answer to an email it received.",
  deal_intro: "Introduces Angela to a deal contact when the plan starts.",
  milestone_ping:
    "Asks the person responsible for a deal step to do it (first ask or follow-up).",
  milestone_question:
    "Passes a contact's question to the realtor, or the realtor's answer back.",
  milestone_blocked: "Tells the realtor a deal step is stuck and needs their help.",
  completion: "Final summary to the realtor when every step is done.",
  print_order_update: "Tells the realtor where their flyer print order stands.",
};

const typeLabel = (emailType) => EMAIL_TYPE_LABELS[emailType] || "Other email";

const SKIP_OPTION = {
  label: "Skip this step & advance the deal",
  action: "skip",
  consequence: "The step is marked skipped and Angela moves the deal to the next step.",
};

const DONT_SEND_OPTION = {
  label: "Don't send",
  action: null,
  consequence: "The email is not sent. Nothing else about the deal changes.",
};

/** Decline choices per email type. `action` is the decline endpoint's `action` (null = omit). */
const DECLINE_OPTIONS = {
  milestone_ping: [
    SKIP_OPTION,
    {
      label: "Don't send this one",
      action: null,
      consequence:
        "A first request is sent again later. A duplicate follow-up is dropped and the existing thread is kept.",
    },
    {
      label: "Start this step over",
      action: "retry",
      consequence:
        "The step's email thread is reset and a fresh first request goes out. Replies to the old thread stop counting.",
    },
  ],
  milestone_blocked: [
    SKIP_OPTION,
    {
      label: "Don't send this one",
      action: null,
      consequence: "Angela raises this again on her next check.",
    },
  ],
  milestone_question: [
    SKIP_OPTION,
    {
      label: "Don't send this one",
      action: null,
      consequence: "The question stays open and Angela raises it again on her next check.",
    },
  ],
};

const declineOptions = (emailType) => DECLINE_OPTIONS[emailType] || [DONT_SEND_OPTION];

const STALE_THRESHOLD_DAYS = 30;

/** Format an ISO timestamp as e.g. "Oct 5, 2026, 3:15 PM" in the viewer's timezone. */
function formatTimestamp(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/** Strip leading Re:, Re[N]:, Fwd: etc. from an email subject. */
function stripReplyPrefix(subject) {
  if (!subject) return "(no subject)";
  const stripped = subject.replace(/^(Re(\[\d+\])?:|Fwd?:)\s*/i, "").trim();
  return stripped || "(no subject)";
}

/**
 * Build thread sub-groups for a set of orphaned emails.
 * Returns an array of { thread_root_id, subject, emails[], hasQueued }.
 */
function buildThreadGroups(emails) {
  const threadMap = new Map();
  for (const email of emails) {
    const key = email.thread_root_id || String(email.id);
    if (!threadMap.has(key)) {
      threadMap.set(key, {
        thread_root_id: key,
        subject: stripReplyPrefix(email.subject),
        emails: [],
        hasQueued: false,
      });
    }
    const thread = threadMap.get(key);
    thread.emails.push(email);
    if (email.status === "queued") thread.hasQueued = true;
  }
  // Sort emails within each thread newest-first once, avoiding repeated sorts in render.
  for (const thread of threadMap.values()) {
    thread.emails.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    thread.latestEmail =
      thread.emails.find((e) => e.status === "queued") || thread.emails[0];
  }
  return Array.from(threadMap.values());
}

/** Deal-less emails group per agent and recipient, so one agent's queue never merges into another's. */
const orphanKey = (email) => `__no_deal__${email.agent || ""}__${email.to_email}`;

function orphanLabel(email) {
  if (email.agent && email.agent !== "deal_coordinator") {
    return `${agentLabel(email)} \u2014 ${email.to_email}`;
  }
  return `Before a deal exists \u2014 ${email.to_email}`;
}

function groupByDeal(emails) {
  const map = new Map();
  for (const email of emails) {
    if (email.deal_id === null || email.deal_id === undefined) {
      // Pre-deal emails: one group per recipient
      const key = orphanKey(email);
      if (!map.has(key)) {
        map.set(key, {
          deal_id: null,
          deal_address: orphanLabel(email),
          key,
          deal_created_by_email: email.deal_created_by_email,
          deal_created_by_name: email.deal_created_by_name,
          isOrphaned: true,
          emails: [],
        });
      }
      map.get(key).emails.push(email);
    } else {
      // Deal-linked emails: group by deal id (unchanged)
      const key = email.deal_id;
      if (!map.has(key)) {
        map.set(key, {
          deal_id: email.deal_id,
          deal_address: email.deal_address || "(Unknown deal)",
          deal_created_by_email: email.deal_created_by_email,
          deal_created_by_name: email.deal_created_by_name,
          isOrphaned: false,
          emails: [],
        });
      }
      map.get(key).emails.push(email);
    }
  }
  return Array.from(map.values());
}

function sortDealGroups(groups, ordering) {
  return [...groups].sort((a, b) => {
    const aMax = Math.max(...a.emails.map((e) => new Date(e.created_at).getTime()));
    const bMax = Math.max(...b.emails.map((e) => new Date(e.created_at).getTime()));
    return ordering === "oldest" ? aMax - bMax : bMax - aMax;
  });
}

const DragHandle = ({ onMouseDown }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onMouseDown={onMouseDown}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        width: 4,
        flexShrink: 0,
        cursor: "col-resize",
        backgroundColor: hovered ? "#28a382" : "#e0e0e0",
        transition: "background-color 0.15s",
      }}
    />
  );
};

const CONTEXT_LABEL_STYLE = { color: "#5f6368", marginRight: 6 };

function DecisionContext({ context }) {
  const { inbound_message: inbound, milestone, deal, marketing_session: marketing } = context;
  if (!inbound && !milestone && !deal && !marketing) return null;
  return (
    <div
      data-testid="decision-context"
      style={{
        padding: "10px 20px",
        borderBottom: "1px solid #e0e0e0",
        backgroundColor: "#f8f9fa",
        fontSize: 13,
        maxHeight: 220,
        overflowY: "auto",
      }}
    >
      {inbound && (
        <div style={{ marginBottom: 8 }}>
          <div>
            <span style={CONTEXT_LABEL_STYLE}>Replying to</span>
            <strong>{inbound.sender}</strong> — {inbound.subject} (
            {new Date(inbound.timestamp).toLocaleString()})
          </div>
          <div style={{ whiteSpace: "pre-wrap", marginTop: 4 }}>{inbound.body_text}</div>
        </div>
      )}
      {milestone && (
        <div style={{ marginBottom: 4 }}>
          <span style={CONTEXT_LABEL_STYLE}>Milestone</span>
          {milestone.name} · {milestone.status_label}
          {milestone.assignee_name || milestone.assignee_email
            ? ` · ${milestone.assignee_name || milestone.assignee_email}`
            : ""}{" "}
          · {milestone.thread_live ? "thread live" : "no live thread"}
          {milestone.pending_question && (
            <div style={{ whiteSpace: "pre-wrap", marginTop: 4 }}>
              <span style={CONTEXT_LABEL_STYLE}>Open question</span>
              {milestone.pending_question}
            </div>
          )}
          {milestone.blocked_notes && (
            <div style={{ whiteSpace: "pre-wrap", marginTop: 4 }}>
              <span style={CONTEXT_LABEL_STYLE}>Blocked because</span>
              {milestone.blocked_notes}
            </div>
          )}
        </div>
      )}
      {marketing && (
        <div>
          <div style={{ marginBottom: 4 }}>
            <span style={CONTEXT_LABEL_STYLE}>Realtor</span>
            {marketing.realtor_name}
            {marketing.realtor_name !== marketing.realtor_email && ` (${marketing.realtor_email})`}
          </div>
          <div style={{ marginBottom: 4 }}>
            <span style={CONTEXT_LABEL_STYLE}>Marketing session</span>
            {marketing.status_label}
            {marketing.address && ` \u00b7 ${marketing.address}`}
          </div>
          {marketing.print_order && (
            <div>
              <span style={CONTEXT_LABEL_STYLE}>Latest print order</span>
              {marketing.print_order.layout} × {marketing.print_order.quantity} ·{" "}
              {marketing.print_order.status_label}
              {marketing.print_order.fulfillment_status &&
                ` \u00b7 ${marketing.print_order.fulfillment_status}`}
              {marketing.print_order.error && ` \u00b7 ${marketing.print_order.error}`}
            </div>
          )}
        </div>
      )}
      {deal && (
        <div>
          <span style={CONTEXT_LABEL_STYLE}>Deal</span>
          {deal.status_label} · {deal.program_status_label || "No program"}
          {deal.active_milestones.length > 0 && ` · active: ${deal.active_milestones.join(", ")}`}
        </div>
      )}
    </div>
  );
}

const AdminDealCoordinator = () => {
  const [allEmails, setAllEmails] = useState([]);
  const [selected, setSelected] = useState(null);
  const [context, setContext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [approving, setApproving] = useState(false);
  const [successMsg, setSuccessMsg] = useState(null);

  // Sidebar state — groups start expanded; this tracks the ones the user collapsed
  const [collapsedDeals, setCollapsedDeals] = useState(new Set());
  const [viewedEmailIds, setViewedEmailIds] = useState(() => new Set());

  // Filter / sort state — default view is the review queue (queued emails only)
  const [showHistory, setShowHistory] = useState(false);
  const pendingOnly = !showHistory;
  const [hideStale, setHideStale] = useState(true);
  const [userFilter, setUserFilter] = useState("");
  const [agentFilter, setAgentFilter] = useState("");
  const [ordering, setOrdering] = useState("newest");

  // Decline state
  const [declineActive, setDeclineActive] = useState(false);
  const [declining, setDeclining] = useState(false);

  // Approve confirmation state
  const [approveActive, setApproveActive] = useState(false);

  // Resizable sidebar state
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const stored = parseInt(localStorage.getItem("dpm_sidebar_width"), 10);
    return isNaN(stored) ? 420 : stored;
  });
  const isDragging = useRef(false);
  const dragStartX = useRef(0);
  const dragStartWidth = useRef(0);
  const containerRef = useRef(null);


  const apiBase = `${process.env.REACT_APP_PAYVIEW_BASE_URL}/api/admin`;

  useEffect(() => {
    fetchEmails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchEmails = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const res = await apiFetch(`${apiBase}/outgoing-emails/`);
      if (!res.ok) throw new Error("Failed to fetch emails");
      const data = await res.json();
      const emails = data.outgoing_emails || [];
      setAllEmails(emails);
      return emails;
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const handleRefresh = async () => {
    const previousId = selected ? selected.id : null;
    setRefreshing(true);
    try {
      const emails = await fetchEmails({ silent: true });
      if (emails === null) return; // fetch failed; error already set

      if (previousId !== null) {
        const refreshed = emails.find((e) => e.id === previousId);
        if (refreshed) {
          setSelected(refreshed);
        } else {
          setSelected(null);
        }
      }
    } finally {
      setRefreshing(false);
    }
  };

  // Decision context is fetched per selected email, not carried on the list.
  // A failed fetch only hides the panel; it never blocks approve/decline.
  useEffect(() => {
    setContext(null);
    if (!selected) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch(`${apiBase}/outgoing-emails/${selected.id}/context/`);
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setContext(data);
      } catch (err) {
        // context is advisory
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selected ? selected.id : null]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectEmail = (email) => {
    setSelected(email);
    setViewedEmailIds((prev) => new Set([...prev, email.id]));
    setDeclineActive(false);
    setApproveActive(false);
    setSuccessMsg(null);
    setError(null);
  };

  // Derive unique agents for user filter dropdown
  const agentOptions = Array.from(
    new Map(
      allEmails
        .filter((e) => e.deal_created_by_email)
        .map((e) => [e.deal_created_by_email, e.deal_created_by_name || e.deal_created_by_email])
    ).entries()
  );

  // Agents that have emails in the queue, for the agent filter dropdown
  const queueAgents = Array.from(
    new Map(allEmails.filter((e) => e.agent).map((e) => [e.agent, agentLabel(e)])).entries()
  );

  // Apply filters and build deal groups
  const filteredEmails = allEmails.filter((e) => {
    if (pendingOnly && e.status !== "queued") return false;
    if (userFilter && e.deal_created_by_email !== userFilter) return false;
    if (agentFilter && e.agent !== agentFilter) return false;
    return true;
  });

  const staleThresholdMs = STALE_THRESHOLD_DAYS * 24 * 60 * 60 * 1000;

  const dealGroups = sortDealGroups(
    groupByDeal(filteredEmails).filter((g) => {
      if (pendingOnly && !g.emails.some((e) => e.status === "queued")) return false;
      if (hideStale) {
        if (g.isOrphaned) {
          // For orphaned groups: stale if all emails are older than threshold
          const maxCreatedAt = Math.max(...g.emails.map((e) => new Date(e.created_at).getTime()));
          if (Date.now() - maxCreatedAt > staleThresholdMs) return false;
        } else {
          const updatedAt = g.emails[0]?.deal_updated_at;
          if (updatedAt && Date.now() - new Date(updatedAt).getTime() > staleThresholdMs) {
            return false;
          }
        }
      }
      return true;
    }),
    ordering
  );

  // Queued emails in the order the sidebar renders them, for auto-advance.
  const queuedInOrder = dealGroups
    .flatMap((g) => (g.isOrphaned ? buildThreadGroups(g.emails).flatMap((t) => t.emails) : g.emails))
    .filter((e) => e.status === "queued");

  /** The next queued email after `id` in sidebar order, wrapping; null if none. */
  const nextQueuedAfter = (id) => {
    const i = queuedInOrder.findIndex((e) => e.id === id);
    const rotated = [...queuedInOrder.slice(i + 1), ...queuedInOrder.slice(0, Math.max(i, 0))];
    return rotated.find((e) => e.id !== id) || null;
  };

  /** After an action, move to the next queued email (or clear) and show the outcome. */
  const advanceFrom = (id, message) => {
    const next = nextQueuedAfter(id);
    if (next) selectEmail(next);
    else setSelected(null);
    setSuccessMsg(message);
  };

  // On first load, open the top queued email the sidebar actually shows — not the
  // API's first, which may sit in a deal hidden by "Hide inactive deals".
  const didAutoSelect = useRef(false);
  useEffect(() => {
    if (loading || didAutoSelect.current) return;
    didAutoSelect.current = true;
    if (queuedInOrder.length > 0) selectEmail(queuedInOrder[0]);
  });

  const toggleDeal = (dealId) => {
    setCollapsedDeals((prev) => {
      const next = new Set(prev);
      if (next.has(dealId)) next.delete(dealId);
      else next.add(dealId);
      return next;
    });
  };

  const typeColors = (emailType) =>
    EMAIL_TYPE_COLORS[emailType] || { bg: "#e8f5e9", color: "#28a382" };

  const handleDragMove = (e) => {
    if (!isDragging.current || !containerRef.current) return;
    const delta = e.clientX - dragStartX.current;
    const containerWidth = containerRef.current.offsetWidth;
    const newWidth = Math.min(
      Math.max(dragStartWidth.current + delta, containerWidth * 0.20),
      containerWidth * 0.60
    );
    setSidebarWidth(newWidth);
  };

  const handleDragEnd = () => {
    if (!isDragging.current) return;
    isDragging.current = false;
    document.removeEventListener("mousemove", handleDragMove);
    document.removeEventListener("mouseup", handleDragEnd);
    document.body.style.userSelect = "";
    setSidebarWidth((w) => {
      localStorage.setItem("dpm_sidebar_width", Math.round(w));
      return w;
    });
  };

  const handleDragStart = (e) => {
    isDragging.current = true;
    dragStartX.current = e.clientX;
    dragStartWidth.current = sidebarWidth;
    document.addEventListener("mousemove", handleDragMove);
    document.addEventListener("mouseup", handleDragEnd);
    document.body.style.userSelect = "none";
  };

  useEffect(() => {
    return () => {
      if (isDragging.current) {
        document.removeEventListener("mousemove", handleDragMove);
        document.removeEventListener("mouseup", handleDragEnd);
        document.body.style.userSelect = "";
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closeAllPanels = () => {
    setApproveActive(false);
    setDeclineActive(false);
  };

  const handleApproveConfirm = async () => {
    setApproving(true);
    setError(null);
    try {
      const res = await apiFetch(`${apiBase}/outgoing-emails/${selected.id}/approve/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      if (!res.ok) throw new Error("Failed to send email");
      const data = await res.json();
      const updatedEmail = data.outgoing_email;
      // Update in-place — keep the email visible with sent status and approval info
      setAllEmails((prev) =>
        prev.map((e) => (e.id === updatedEmail.id ? updatedEmail : e))
      );
      setApproveActive(false);
      advanceFrom(updatedEmail.id, `Sent "${updatedEmail.subject}" to ${updatedEmail.to_email}.`);
    } catch (err) {
      setError(err.message);
    } finally {
      setApproving(false);
    }
  };

  const handleDecline = async (action) => {
    setDeclining(true);
    setError(null);
    try {
      const body = action ? { action } : {};
      const res = await apiFetch(`${apiBase}/outgoing-emails/${selected.id}/decline/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error("Failed to decline email");
      const data = await res.json();
      const updatedEmail = data.outgoing_email;
      setAllEmails((prev) =>
        prev.map((e) => (e.id === updatedEmail.id ? updatedEmail : e))
      );
      setDeclineActive(false);
      advanceFrom(updatedEmail.id, `Declined "${updatedEmail.subject}". It was not sent.`);
    } catch (err) {
      setError(err.message);
    } finally {
      setDeclining(false);
    }
  };

  const isSent = selected && selected.status === "sent";
  const isDeclined = selected && selected.status === "declined";
  // Visible queued emails only — must agree with what the sidebar shows.
  const anyQueued = queuedInOrder.length > 0;

  if (loading) {
    return (
      <div className="client-list-root" style={{ textAlign: "left" }}>
        <div className="client-list-container">
          <div
            className="client-list-heading"
            style={{ borderBottom: "1px solid #e0e0e0", paddingBottom: 16 }}
          >
            <div className="lc">
              <h3 className="cl-rh">{PAGE_TITLE}</h3>
              <p className="cl-rh-sub">{PAGE_SUBTITLE}</p>
            </div>
          </div>
          <p className="loading">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="client-list-root" style={{ textAlign: "left" }}>
      <div className="client-list-container">
        {/* Page heading */}
        <div
          className="client-list-heading"
          style={{ borderBottom: "1px solid #e0e0e0", paddingBottom: 16, display: "flex", alignItems: "center" }}
        >
          <div className="lc" style={{ flex: 1 }}>
            <h3 className="cl-rh">{PAGE_TITLE}</h3>
            <p className="cl-rh-sub">{PAGE_SUBTITLE}</p>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            aria-label="Refresh emails"
            style={{
              padding: "10px 18px",
              background: "transparent",
              border: "1px solid #28a382",
              borderRadius: 4,
              cursor: refreshing ? "not-allowed" : "pointer",
              opacity: refreshing ? 0.6 : 1,
              color: "#28a382",
              fontSize: 14,
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
            }}
          >
            <RefreshIcon style={{ fontSize: 16, marginRight: 6, verticalAlign: "middle" }} />
            {refreshing ? "Refreshing\u2026" : "Refresh"}
          </button>
        </div>

        {/* Outcome of the last action — page-level, since the handled email leaves the queue */}
        {successMsg && (
          <div
            role="status"
            style={{
              marginTop: 12,
              padding: "8px 14px",
              borderRadius: 4,
              backgroundColor: "#e8f5e9",
              color: "#1a7a4a",
              fontSize: 14,
            }}
          >
            {successMsg}
          </div>
        )}

        {/* Resizable panel row */}
        <div ref={containerRef} style={{ display: "flex", height: "calc(100vh - 200px)", marginTop: 20 }}>

          {/* Left panel — grouped sidebar */}
          <div
            style={{
              width: sidebarWidth,
              flexShrink: 0,
              display: "flex",
              flexDirection: "column",
              backgroundColor: "#fff",
              overflow: "hidden",
            }}
          >
            {/* Filter bar */}
            <div
              style={{
                padding: "10px 14px",
                borderBottom: "1px solid #e8e8e8",
                display: "flex",
                flexDirection: "column",
                gap: 8,
                flexShrink: 0,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <button
                  onClick={() => setShowHistory((v) => !v)}
                  title="Also show emails that were already sent or declined"
                  style={{
                    fontSize: 13,
                    padding: "4px 10px",
                    borderRadius: 4,
                    border: showHistory ? "1px solid #1a7a4a" : "1px solid #ccc",
                    background: showHistory ? "#1a7a4a" : "#fff",
                    color: showHistory ? "#fff" : "#555",
                    cursor: "pointer",
                    fontWeight: showHistory ? 600 : 400,
                  }}
                >
                  Show history
                </button>
                <button
                  onClick={() => setHideStale((v) => !v)}
                  title={`Hide deals with no activity in the last ${STALE_THRESHOLD_DAYS} days`}
                  style={{
                    fontSize: 13,
                    padding: "4px 10px",
                    borderRadius: 4,
                    border: hideStale ? "1px solid #1a7a4a" : "1px solid #ccc",
                    background: hideStale ? "#1a7a4a" : "#fff",
                    color: hideStale ? "#fff" : "#555",
                    cursor: "pointer",
                    fontWeight: hideStale ? 600 : 400,
                  }}
                >
                  Hide inactive deals
                </button>
                <select
                  value={ordering}
                  onChange={(e) => setOrdering(e.target.value)}
                  style={{ fontSize: 14, padding: "3px 6px", border: "1px solid #ccc", borderRadius: 4, marginLeft: "auto" }}
                >
                  <option value="newest">Newest first</option>
                  <option value="oldest">Oldest first</option>
                </select>
              </div>
              {queueAgents.length > 1 && (
                <select
                  aria-label="Filter by agent"
                  value={agentFilter}
                  onChange={(e) => setAgentFilter(e.target.value)}
                  style={{ fontSize: 14, padding: "3px 6px", border: "1px solid #ccc", borderRadius: 4, marginRight: 8 }}
                >
                  <option value="">All agents</option>
                  {queueAgents.map(([agent, label]) => (
                    <option key={agent} value={agent}>{label}</option>
                  ))}
                </select>
              )}
              {agentOptions.length > 0 && (
                <select
                  value={userFilter}
                  onChange={(e) => setUserFilter(e.target.value)}
                  style={{ fontSize: 14, padding: "3px 6px", border: "1px solid #ccc", borderRadius: 4 }}
                >
                  <option value="">All realtors</option>
                  {agentOptions.map(([email, name]) => (
                    <option key={email} value={email}>{name}</option>
                  ))}
                </select>
              )}
            </div>

            {/* Deal tree */}
            <div style={{ flex: 1, overflowY: "auto" }}>
              {dealGroups.length === 0 ? (
                <div style={{ padding: 20, textAlign: "center", color: "#666", fontSize: 15 }}>
                  {showHistory ? "No emails." : "No emails waiting for approval."}
                </div>
              ) : (
                dealGroups.map((group) => {
                  const groupKey = group.isOrphaned ? group.key : group.deal_id;
                  const isExpanded = !collapsedDeals.has(groupKey);
                  const hasQueued = group.emails.some((e) => e.status === "queued");

                  return (
                    <div key={groupKey}>
                      {/* Group header node */}
                      <div
                        onClick={() => toggleDeal(groupKey)}
                        style={{
                          padding: "10px 14px",
                          borderBottom: "1px solid #f0f0f0",
                          cursor: "pointer",
                          backgroundColor: "#f9f9f9",
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          userSelect: "none",
                        }}
                      >
                        <span style={{ fontSize: 13, color: "#888", width: 12 }}>
                          {isExpanded ? "▼" : "▶"}
                        </span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            style={{
                              fontSize: 15,
                              fontWeight: 600,
                              color: "#222",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {group.deal_address}
                          </div>
                          {!group.isOrphaned && group.deal_created_by_name && (
                            <div style={{ fontSize: 13, color: "#888", marginTop: 1 }}>
                              {group.deal_created_by_name}
                            </div>
                          )}
                        </div>
                        {hasQueued && (
                          <span
                            style={{
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              background: "#28a382",
                              flexShrink: 0,
                            }}
                          />
                        )}
                      </div>

                      {/* Expanded content */}
                      {isExpanded && (
                        group.isOrphaned
                          ? /* Orphaned group: render thread rows */
                            buildThreadGroups(
                              pendingOnly ? group.emails.filter((e) => e.status === "queued") : group.emails
                            ).map((thread) => {
                              const threadEmailIds = thread.emails.map((e) => e.id);
                              // latestEmail is pre-computed in buildThreadGroups (most recent queued, else most recent).
                              const emailToSelect = thread.latestEmail;
                              const isThreadSelected = selected && threadEmailIds.includes(selected.id);
                              const isThreadUnviewed = thread.hasQueued && emailToSelect != null && !viewedEmailIds.has(emailToSelect.id);
                              return (
                                <div
                                  key={thread.thread_root_id}
                                  onClick={() => emailToSelect && selectEmail(emailToSelect)}
                                  style={{
                                    padding: "10px 14px 10px 28px",
                                    borderBottom: "1px solid #f5f5f5",
                                    cursor: emailToSelect ? "pointer" : "default",
                                    backgroundColor: isThreadSelected ? "#e8f5e9" : isThreadUnviewed ? "#f0fdf4" : "#fff",
                                    borderLeft: isThreadSelected ? "4px solid #28a382" : "4px solid transparent",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 6,
                                  }}
                                >
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div
                                      style={{
                                        fontSize: 14,
                                        fontWeight: 500,
                                        color: "#333",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {thread.subject}
                                    </div>
                                    <div style={{ fontSize: 12, color: "#aaa", marginTop: 2 }}>
                                      {thread.emails.length} email{thread.emails.length !== 1 ? "s" : ""}
                                      {" "}
                                      <AgentTag email={thread.latestEmail} />
                                    </div>
                                  </div>
                                  {thread.hasQueued && (
                                    <span
                                      style={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: "50%",
                                        background: "#28a382",
                                        flexShrink: 0,
                                      }}
                                    />
                                  )}
                                </div>
                              );
                            })
                          : /* Deal-linked group: render individual email rows (unchanged) */
                            (pendingOnly ? group.emails.filter((e) => e.status === "queued") : group.emails)
                              .map((email) => {
                                const colors = typeColors(email.email_type);
                                const isSelected = selected && selected.id === email.id;
                                const isUnviewed = email.status === "queued" && !viewedEmailIds.has(email.id);
                                return (
                                  <div
                                    key={email.id}
                                    onClick={() => selectEmail(email)}
                                    style={{
                                      padding: "10px 14px 10px 28px",
                                      borderBottom: "1px solid #f5f5f5",
                                      cursor: "pointer",
                                      backgroundColor: isSelected ? "#e8f5e9" : isUnviewed ? "#f0fdf4" : "#fff",
                                      borderLeft: isSelected ? "4px solid #28a382" : "4px solid transparent",
                                      opacity: email.status === "declined" ? 0.5 : 1,
                                    }}
                                  >
                                    <div style={{ marginBottom: 3 }}>
                                      <span
                                        style={{
                                          padding: "1px 6px",
                                          backgroundColor: colors.bg,
                                          color: colors.color,
                                          borderRadius: 4,
                                          fontSize: 12,
                                          fontWeight: 600,
                                        }}
                                      >
                                        {typeLabel(email.email_type)}
                                      </span>
                                      <span style={{ marginLeft: 6 }}>
                                        <AgentTag email={email} />
                                      </span>
                                      {email.status === "sent" && (
                                        <span style={{ marginLeft: 6, fontSize: 12, color: "#999" }}>✓ sent</span>
                                      )}
                                      {email.status === "declined" && (
                                        <span style={{ marginLeft: 6, fontSize: 12, color: "#c0392b" }}>✗ declined</span>
                                      )}
                                    </div>
                                    <div
                                      style={{
                                        fontSize: 14,
                                        fontWeight: 500,
                                        color: "#333",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {email.subject}
                                    </div>
                                    <div style={{ fontSize: 13, color: "#888", marginTop: 2 }}>
                                      {email.to_email}
                                    </div>
                                    <div style={{ fontSize: 12, color: "#999", marginTop: 2 }}>
                                      {formatTimestamp(email.created_at)}
                                    </div>
                                  </div>
                                );
                              })
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Drag handle */}
          <DragHandle onMouseDown={handleDragStart} />

          {/* Right panel */}
          <div
            style={{
              flex: 1,
              minWidth: 0,
              backgroundColor: "#fafafa",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            {!selected ? (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  color: "#999",
                }}
              >
                {anyQueued ? "Select an email to review it." : "Nothing left to review."}
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    flex: 1,
                    margin: "20px 24px 20px",
                    border: "1px solid #e0e0e0",
                    borderRadius: 8,
                    overflow: "hidden",
                  }}
                >

                {/* Gmail-style email header */}
                <div
                  style={{
                    padding: "16px 20px",
                    backgroundColor: "#fff",
                    borderBottom: "1px solid #e0e0e0",
                    flexShrink: 0,
                  }}
                >
                  <div style={{ fontSize: 18, fontWeight: 600, color: "#202124", marginBottom: 12 }}>
                    {selected.subject}
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    {[
                      { label: "From", value: selected.from_email },
                      { label: "To", value: selected.to_email },
                      { label: "Date", value: formatTimestamp(selected.sent_at || selected.created_at) },
                      ...(selected.cc_emails && selected.cc_emails.length > 0
                        ? [{ label: "CC", value: selected.cc_emails.join(", ") }]
                        : []),
                    ].map(({ label, value }) => (
                      <div key={label} style={{ display: "flex", gap: 8, fontSize: 13 }}>
                        <span style={{ width: 36, color: "#5f6368", flexShrink: 0 }}>{label}</span>
                        <span style={{ color: "#202124" }}>{value}</span>
                      </div>
                    ))}
                  </div>
                  <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 8 }}>
                    <span
                      style={{
                        padding: "2px 8px",
                        backgroundColor: typeColors(selected.email_type).bg,
                        color: typeColors(selected.email_type).color,
                        borderRadius: 4,
                        fontSize: 11,
                        fontWeight: 600,
                      }}
                    >
                      {typeLabel(selected.email_type)}
                    </span>
                    <AgentTag email={selected} />
                    {selected.deal_address && (
                      <span style={{ fontSize: 12, color: "#5f6368" }}>{selected.deal_address}</span>
                    )}
                    {selected.milestone_name && (
                      <span style={{ fontSize: 12, color: "#5f6368" }}>— {selected.milestone_name}</span>
                    )}
                  </div>
                  {EMAIL_TYPE_PURPOSE[selected.email_type] && (
                    <div style={{ marginTop: 6, fontSize: 13, color: "#5f6368" }}>
                      {EMAIL_TYPE_PURPOSE[selected.email_type]}
                    </div>
                  )}
                </div>

                {context && <DecisionContext context={context} />}

                {/* Email preview */}
                <iframe
                  srcDoc={selected.html_content}
                  title="Email preview"
                  style={{ flex: 1, border: "none", borderBottom: "1px solid #e0e0e0" }}
                />

                {/* Bottom controls strip */}
                <div
                  style={{
                    padding: "12px 20px 16px",
                    backgroundColor: "#fff",
                    borderTop: "1px solid #e0e0e0",
                    flexShrink: 0,
                  }}
                >
                  {error && (
                    <div style={{ color: "#c0392b", marginBottom: 10, fontSize: 13 }}>{error}</div>
                  )}

                  {/* Approval attribution for sent emails */}
                  {isSent && selected.approval && (
                    <div style={{ fontSize: 12, color: "#5f6368", marginBottom: 10 }}>
                      Approved by {selected.approval.approved_by_name} on{" "}
                      {new Date(selected.approval.approved_at).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </div>
                  )}

                  {/* Decline confirmation panel */}
                  {declineActive && (
                    <div
                      style={{
                        marginBottom: 12,
                        padding: "12px 14px",
                        border: "1px solid #e0b0b0",
                        borderRadius: 6,
                        backgroundColor: "#fff8f8",
                      }}
                    >
                      <div style={{ fontSize: 13, color: "#5f1a1a", marginBottom: 10, fontWeight: 500 }}>
                        Decline this email?
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        {declineOptions(selected.email_type).map((option) => (
                          <div key={option.label} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <button
                              onClick={() => handleDecline(option.action)}
                              disabled={declining}
                              style={{
                                padding: "6px 14px",
                                background: "#c0392b",
                                color: "#fff",
                                border: "none",
                                borderRadius: 4,
                                cursor: declining ? "not-allowed" : "pointer",
                                fontSize: 13,
                                fontWeight: 600,
                                flexShrink: 0,
                              }}
                            >
                              {option.label}
                            </button>
                            <span style={{ fontSize: 12, color: "#5f1a1a" }}>{option.consequence}</span>
                          </div>
                        ))}
                      </div>
                      <div style={{ marginTop: 10 }}>
                        <button
                          onClick={() => setDeclineActive(false)}
                          disabled={declining}
                          style={{
                            padding: "6px 14px",
                            background: "transparent",
                            border: "1px solid #ccc",
                            borderRadius: 4,
                            cursor: declining ? "not-allowed" : "pointer",
                            fontSize: 13,
                            color: "#555",
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Approve confirmation panel */}
                  {approveActive && (
                    <div
                      style={{
                        marginBottom: 12,
                        padding: "12px 14px",
                        border: "1px solid #b0d8c0",
                        borderRadius: 6,
                        backgroundColor: "#f8fff8",
                      }}
                    >
                      <div style={{ fontSize: 13, color: "#1a4a2e", marginBottom: 10, fontWeight: 500 }}>
                        Send this email?
                      </div>
                      <div style={{ fontSize: 12, color: "#1a4a2e", marginBottom: 10 }}>
                        It goes to {selected.to_email}
                        {selected.cc_emails && selected.cc_emails.length > 0
                          ? ` (CC ${selected.cc_emails.join(", ")})`
                          : ""}{" "}
                        right away and can&apos;t be unsent.
                      </div>
                      <div style={{ display: "flex", gap: 8 }}>
                        <button
                          onClick={handleApproveConfirm}
                          disabled={approving}
                          style={{
                            padding: "6px 14px",
                            background: "#28a382",
                            color: "#fff",
                            border: "none",
                            borderRadius: 4,
                            cursor: approving ? "not-allowed" : "pointer",
                            fontSize: 13,
                            fontWeight: 600,
                          }}
                        >
                          {approving ? "Sending…" : "Confirm & Send"}
                        </button>
                        <button
                          onClick={() => setApproveActive(false)}
                          disabled={approving}
                          style={{
                            padding: "6px 14px",
                            background: "transparent",
                            border: "1px solid #ccc",
                            borderRadius: 4,
                            cursor: approving ? "not-allowed" : "pointer",
                            fontSize: 13,
                            color: "#555",
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  <div style={{ display: "flex", gap: 10 }}>
                    {/* Decline button */}
                    <button
                      onClick={!isSent && !isDeclined ? () => { const wasOpen = declineActive; closeAllPanels(); if (!wasOpen) setDeclineActive(true); } : undefined}
                      disabled={isSent || isDeclined}
                      style={{
                        padding: "8px 20px",
                        background: "transparent",
                        border: isSent || isDeclined ? "1px solid #e0e0e0" : "1px solid #c0392b",
                        borderRadius: 4,
                        cursor: isSent || isDeclined ? "not-allowed" : "pointer",
                        fontSize: 14,
                        color: isSent || isDeclined ? "#aaa" : "#c0392b",
                      }}
                    >
                      Decline
                    </button>

                    {/* Approve & Send button */}
                    <button
                      onClick={!isSent && !isDeclined ? () => { const wasOpen = approveActive; closeAllPanels(); if (!wasOpen) setApproveActive(true); } : undefined}
                      disabled={isSent || isDeclined || approving}
                      style={{
                        padding: "8px 20px",
                        background: isSent || isDeclined ? "#ccc" : "#28a382",
                        color: "#fff",
                        border: "none",
                        borderRadius: 4,
                        cursor: isSent || isDeclined || approving ? "not-allowed" : "pointer",
                        fontSize: 14,
                        fontWeight: 600,
                      }}
                    >
                      Approve &amp; Send
                    </button>
                  </div>
                </div>
                </div>

              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

export default AdminDealCoordinator;
